import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert } from '@cmsgov/design-system';
import { selectContractApplication, useMyApplicationsQuery, useUserQuery } from '@access-cms/api-client';

import { PageLayout } from '../../components/PageLayout';
import { Button, ButtonVariation } from '../../components/Button';
import { ChevronDirection, ChevronIcon } from '../../components/Icon';
import { Spinner } from '../../components/Spinner';
import { useAuth } from '../../components/auth/AuthContext';
import ApplicationPicker from './components/ApplicationPicker';
import { APPLICATION_TEST_MODE_PARAM, APPLICATION_TITLE } from './constants';
import { ApplicationStatus, useApplication } from './context';
import { useApplicationSelection } from './hooks/useApplicationSelection';
import ApplicationWorkflowLayout from './layouts/ApplicationWorkflowLayout';

import './Application.css';

function Application() {
  const baseTestId = 'application-page';
  const [searchParams] = useSearchParams();
  const isTestModeEnabled = searchParams.get(APPLICATION_TEST_MODE_PARAM) === 'true';
  const hasHydratedRef = useRef(false);
  // undefined = no selection tracked yet this mount; reset only on real changes
  const lastHydratedContractIdRef = useRef<string | null | undefined>(undefined);

  const {
    applicationStatus,
    currentStepIndex,
    hydrateFromServer,
    prepareNewApplication,
    serverApplicationStatus,
    showSuccessAlert,
    startWorkflow,
    resetWorkflow,
    viewApplication,
    workflowAlert,
  } = useApplication();

  const query = useMyApplicationsQuery({
    enabled: !isTestModeEnabled,
  });

  const { candidateIds, selectContract, selectedContractId } = useApplicationSelection(query.data?.applications);
  const selected = selectContractApplication(query.data, selectedContractId);
  const { user: authUser } = useAuth();

  // An entry without an application has no userId yet. The signed-in user is
  // the one starting it, so their profile seeds the new application the same
  // way it seeds a server-created draft.
  const isStartableEntry = !!selected && !selected.application && !selected.reason;
  const userId = selected?.application?.userId ?? (isStartableEntry ? authUser?.id : undefined) ?? '';

  const userQuery = useUserQuery(userId, {
    enabled: !!userId,
  });

  const initError = query.error;
  const isInitializing = !isTestModeEnabled && (query.isLoading || (userId !== '' && userQuery.isLoading));

  useEffect(() => {
    // Switching between actual contracts mid-session restarts the workflow.
    // `null` is the deliberate multi-contract picker state, not an application,
    // so the first explicit choice must hydrate in place rather than clear the
    // landing page and make the selection feel like a full reload.
    if (
      lastHydratedContractIdRef.current !== undefined &&
      lastHydratedContractIdRef.current !== null &&
      selectedContractId !== lastHydratedContractIdRef.current
    ) {
      hasHydratedRef.current = false;
      resetWorkflow();
    }
    lastHydratedContractIdRef.current = selectedContractId;

    if (!selected || hasHydratedRef.current) return;

    if (selected.application && userQuery.data) {
      hydrateFromServer(selected.application, userQuery.data, selected.contract, selected.laborCategory);
      hasHydratedRef.current = true;
    } else if (isStartableEntry && selectedContractId) {
      // No application to hydrate: seed the ids the workflow's create call
      // needs, plus the profile once it loads. A failed profile lookup still
      // lets the user begin.
      if (userId && userQuery.isLoading) return;
      prepareNewApplication(selectedContractId, userQuery.data ?? null, selected.contract, selected.laborCategory);
      hasHydratedRef.current = true;
    }
  }, [
    selectedContractId,
    selected,
    isStartableEntry,
    userId,
    hydrateFromServer,
    prepareNewApplication,
    resetWorkflow,
    userQuery.data,
    userQuery.isLoading,
  ]);

  if (currentStepIndex >= 0) {
    return <ApplicationWorkflowLayout data-testid={`${baseTestId}-workflow`} />;
  }

  if (isInitializing) {
    return (
      <PageLayout
        data-testid={baseTestId}
        mainAriaLabelledby='page-heading'
        mainId='content'
        mainTabIndex={-1}>
        <h1
          data-testid={`${baseTestId}-title`}
          id='page-heading'>
          {APPLICATION_TITLE}
        </h1>
        <div
          className='ds-u-display--flex ds-u-align-items--center ds-u-padding-y--4'
          data-testid={`${baseTestId}-loading`}>
          <Spinner
            ariaLabel='Checking application status'
            data-testid={`${baseTestId}-spinner`}
          />
          <span className='ds-u-margin-left--2'>Checking your application status&hellip;</span>
        </div>
      </PageLayout>
    );
  }

  // Server-level reason (no candidate contracts at all) or the selected
  // entry's own reason (e.g. MISSING_LABOR_CATEGORY). With multiple
  // candidates and none selected, the required picker prompts the choice
  // instead of an error alert.
  const initReason = query.data?.reason ?? selected?.reason;
  const isIneligible = !!initReason;
  const requiresSelection = !isTestModeEnabled && !selected && candidateIds.length > 1;

  const application = selected?.application;

  // A single VAS region (fingerprints or eApp) timing out can leave the app
  // parked at VAS_AWAITING_FINGERPRINTS_OR_EAPP (before the workflow-engine
  // escalation lands) or escalated to VAS_TIMEOUT (case cancelled in
  // ServiceNow) — either way, applicationSubStatus carries the region's
  // 'timeout' marker. Mirrors the backend's isVasRegionTimeoutResubmittable
  // (application-sub-status-state/transition-maps.ts): a healthy in-flight
  // verification or a plain 14-day full-case VAS_TIMEOUT (no region marker)
  // must NOT be treated as editable.
  const hasVasRegionTimeout =
    application?.applicationSubStatus?.fingerprints === 'timeout' ||
    application?.applicationSubStatus?.eapp === 'timeout';
  const isVasTimeoutResubmittable =
    (serverApplicationStatus === 'VAS_AWAITING_FINGERPRINTS_OR_EAPP' || serverApplicationStatus === 'VAS_TIMEOUT') &&
    hasVasRegionTimeout;

  // Every returned variant re-opens the application for the applicant: COR
  // return (RETURNED), FNMS return, and VAS return. A resubmission re-enters
  // the pipeline (→ SUBMITTED), so a resubmitted app naturally renders as
  // submitted again.
  const RETURNED_STATUSES = ['RETURNED', 'FNMS_RETURNED', 'VAS_RETURNED'];
  const isReturned =
    serverApplicationStatus !== null &&
    (RETURNED_STATUSES.includes(serverApplicationStatus) || isVasTimeoutResubmittable);
  const isSubmitted = serverApplicationStatus !== null && serverApplicationStatus !== 'DRAFT' && !isReturned;
  const isInProgress = applicationStatus === ApplicationStatus.IN_PROGRESS;
  const isPristineDraft =
    application?.applicationStatus === 'DRAFT' &&
    application.updatedAt === null &&
    Object.keys(application.applicationData).length === 0;

  let buttonText = 'Begin Application';
  if (isSubmitted) {
    buttonText = 'View Application';
  } else if (isInProgress && !isPristineDraft) {
    buttonText = 'Continue Application';
  }

  const handleButtonClick = () => {
    if (isSubmitted) {
      viewApplication();
    } else {
      startWorkflow();
    }
  };

  const handleContinueFromAlert = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    handleButtonClick();
  };

  const isAuthError = !!initError && (initError.statusCode === 401 || initError.statusCode === 403);
  const shouldShowInitError = !isTestModeEnabled && !!initError;

  const errorAlert = shouldShowInitError && (
    <Alert
      className='ds-u-margin-bottom--3'
      data-testid={`${baseTestId}-error-alert`}
      heading='Unable to Check Application Status'
      variation='error'>
      <p className='ds-u-margin-bottom--0'>
        {isAuthError
          ? 'Your session could not be verified. Please sign out and sign back in, then try again.'
          : 'There was an error checking your application status. Please refresh the page or try again later. If the problem persists, contact support.'}
      </p>
    </Alert>
  );

  const ineligibleContentByReason: Record<string, { heading: string; body: string }> = {
    MISSING_LABOR_CATEGORY: {
      body: 'You must have a labor category assigned to your roster membership before starting an application. Please contact your Roster Owner for assistance.',
      heading: 'Labor Category Required',
    },
  };

  const ineligibleContent = (initReason && ineligibleContentByReason[initReason]) || {
    body: 'You must be added to the contract roster before you can begin an application. Please contact your Roster Owner to request access.',
    heading: 'Not on Contract Roster',
  };

  const ineligibleAlert = isIneligible && (
    <Alert
      className='ds-u-margin-bottom--3'
      data-testid={`${baseTestId}-ineligible-alert`}
      heading={ineligibleContent.heading}
      variation='error'>
      <p className='ds-u-margin-bottom--0'>{ineligibleContent.body}</p>
    </Alert>
  );

  const returnedAlert = isReturned && (
    <Alert
      className='ds-u-margin-bottom--3'
      data-testid={`${baseTestId}-returned-alert`}
      heading={
        isVasTimeoutResubmittable
          ? 'Your application needs attention.'
          : 'Your application has been returned for updates.'
      }
      variation='warn'>
      <p className='ds-u-margin-bottom--0'>
        {isVasTimeoutResubmittable
          ? 'Your fingerprinting or eApp verification did not complete in time. Please click "Continue Application" below to review your application and resubmit it.'
          : 'Your application has been reviewed and returned with requested changes. Please click "Continue Application" below to review the feedback, make the necessary updates, and re-submit your application.'}
      </p>
    </Alert>
  );

  const savedAlert = showSuccessAlert && (
    <Alert
      className='ds-u-margin-bottom--3'
      data-testid={`${baseTestId}-alert`}
      heading='Your application has been saved.'
      variation='success'>
      <p className='ds-u-margin-bottom--0'>
        To return to your application and continue completing it, click the{' '}
        <a
          className='ds-c-link'
          href='#continue'
          onClick={handleContinueFromAlert}>
          Continue Application
        </a>{' '}
        button below.
      </p>
    </Alert>
  );

  const workflowAlertContent = workflowAlert && (
    <Alert
      className='ds-u-margin-bottom--3'
      data-testid={`${baseTestId}-workflow-alert`}
      heading={workflowAlert.heading}
      variation={workflowAlert.variation}>
      <p className='ds-u-margin-bottom--0'>{workflowAlert.text}</p>
    </Alert>
  );

  const mainContent = (
    <>
      <h1
        className='application-startup__heading'
        data-testid={`${baseTestId}-title`}
        id='page-heading'>
        Welcome to the {APPLICATION_TITLE}
      </h1>
      <div className='application-startup__body'>
        <p>Congratulations! You have been invited to set up a CMS Workforce (EUA) ID.</p>
        <p>
          Please complete the application as soon as possible so you can access the CMS systems you need for your job.
        </p>
        <p>Make sure you have the following items ready before you apply:</p>
        <ul>
          <li>
            <strong>PIV training:</strong>
            {' A completed and signed'}
            <a
              className='application-startup__piv-link ds-c-link'
              href='https://www.cms.gov/About-CMS/Contracting-With-CMS/ContractingGeneralInformation/Downloads/PolicyandGuidance/PIV-Applicant-Training.pptx'>
              {' Personal Identification Card (PIV) training'}
            </a>
            {' with a signed certificate page'}
          </li>
          <li>
            <strong>Resume:</strong> An updated resume file
          </li>
          <li>
            <strong>Immigration documents (for foreign nationals only):</strong> Passport and Proof of Residency (i.e.,
            visa).
          </li>
        </ul>
        <p>
          Choose your application or click the button below to access your Application. You must fill out all required
          fields and upload your documents to submit your application.
        </p>
      </div>

      {/* Only present a choice when the user actually has multiple
          contracts/applications to act on. A single entry is auto-selected
          by useApplicationSelection and shown inline in the landing content. */}
      {!isTestModeEnabled && candidateIds.length > 1 && query.data && (
        <ApplicationPicker
          applications={query.data.applications}
          data-testid={`${baseTestId}-picker`}
          selectedContractId={selectedContractId}
          onSelect={selectContract}
        />
      )}

      <div className='ds-u-margin-top--4'>
        <Button
          isAutoWidth
          aria-label={`${buttonText} - proceed to application form`}
          data-testid={`${baseTestId}-start`}
          disabled={isIneligible || !!initError || requiresSelection}
          variation={ButtonVariation.SOLID}
          onClick={handleButtonClick}>
          {buttonText}
          <span aria-hidden='true'>
            <ChevronIcon
              direction={ChevronDirection.RIGHT}
              size={16}
            />
          </span>
        </Button>
      </div>
    </>
  );

  return (
    <PageLayout
      alert={errorAlert || ineligibleAlert || returnedAlert || savedAlert || workflowAlertContent}
      data-testid={baseTestId}
      mainAriaLabelledby='page-heading'
      mainId='content'
      mainTabIndex={-1}>
      {mainContent}
    </PageLayout>
  );
}

export default Application;
